import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FileText, Shield, CreditCard, Package, AlertCircle } from 'lucide-react';

const Terms = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '900px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '18px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const lastUpdatedStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px',
    fontStyle: 'italic',
    marginTop: '12px'
  };

  const sectionStyle = {
    marginBottom: '48px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px',
    marginBottom: '16px'
  };

  const subsectionTitleStyle = {
    color: themeColors.text,
    fontSize: '18px',
    fontWeight: '600',
    marginTop: '24px',
    marginBottom: '12px'
  };

  const linkStyle = {
    color: themeColors.primary,
    textDecoration: 'none',
    fontWeight: '500'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Terms of Service</h1>
          <p style={subtitleStyle}>
            Please read these terms carefully before using BabyResell.
          </p>
          <p style={lastUpdatedStyle}>Last updated: March 2026</p>
        </div>

        {/* Introduction */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <FileText size={28} color={themeColors.primary} />
            Agreement to Terms
          </h2>
          <p style={paragraphStyle}>
            By accessing or using BabyResell ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Platform.
          </p>
          <p style={paragraphStyle}>
            BabyResell is operated by Interverse ("we," "us," or "our"). These Terms govern your use of our website, mobile applications, and services.
          </p>
        </div>

        {/* Account Registration */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Shield size={28} color={themeColors.primary} />
            Account Registration & Eligibility
          </h2>
          <p style={paragraphStyle}>
            To use certain features of BabyResell, you must register for an account. You agree to:
          </p>
          <ul style={listStyle}>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Keep your password secure and confidential</li>
            <li>Notify us immediately of any unauthorized account access</li>
            <li>Be at least 18 years old or have parental/guardian consent</li>
          </ul>
          <p style={paragraphStyle}>
            You are responsible for all activities that occur under your account. We reserve the right to suspend or terminate accounts that violate these Terms.
          </p>
        </div>

        {/* Buying and Selling */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <Package size={28} color={themeColors.primary} />
            Buying & Selling on BabyResell
          </h2>

          <h3 style={subsectionTitleStyle}>For Sellers:</h3>
          <p style={paragraphStyle}>When listing items for sale, you agree to:</p>
          <ul style={listStyle}>
            <li>Provide accurate descriptions and photos of items</li>
            <li>Disclose any defects, damage, or safety concerns</li>
            <li>Only list items you legally own and have the right to sell</li>
            <li>Not list prohibited items (recalled products, expired car seats, etc.)</li>
            <li>Ship items promptly after sale (within 3 business days recommended)</li>
            <li>Communicate professionally with buyers</li>
            <li>Comply with all applicable laws and regulations</li>
          </ul>

          <h3 style={subsectionTitleStyle}>For Buyers:</h3>
          <p style={paragraphStyle}>When purchasing items, you agree to:</p>
          <ul style={listStyle}>
            <li>Pay for items promptly after purchase</li>
            <li>Inspect items upon delivery and report issues within 48 hours</li>
            <li>Communicate professionally with sellers</li>
            <li>Not abuse the buyer protection or dispute process</li>
            <li>Confirm receipt of items in good faith</li>
          </ul>
        </div>

        {/* Payments & Fees */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <CreditCard size={28} color={themeColors.primary} />
            Payments, Fees & Refunds
          </h2>
          <p style={paragraphStyle}>
            <strong>Platform Fee:</strong> BabyResell charges an 8% platform fee on completed sales. This fee is deducted from the seller's payout and covers payment processing, platform maintenance, and buyer protection.
          </p>
          <p style={paragraphStyle}>
            <strong>Payment Processing:</strong> All payments are processed securely through Stripe. By using our Platform, you agree to Stripe's terms and conditions.
          </p>
          <p style={paragraphStyle}>
            <strong>Escrow:</strong> Buyer payments are held in escrow until the buyer confirms delivery or after an automatic 48-hour release period.
          </p>
          <p style={paragraphStyle}>
            <strong>Refunds:</strong> Refunds are issued at our discretion in cases of disputes, misrepresented items, or canceled transactions. Sellers may be required to issue refunds if items are not as described.
          </p>
        </div>

        {/* Prohibited Items */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>
            <AlertCircle size={28} color={themeColors.error} />
            Prohibited Items & Conduct
          </h2>
          <h3 style={subsectionTitleStyle}>You may not list or sell:</h3>
          <p style={paragraphStyle}>
            For the safety of babies and families, the following items are strictly prohibited on BabyResell. Listings containing these items will be removed and may result in account suspension.
          </p>
          <ul style={listStyle}>
            <li><strong>Inclined sleepers:</strong> Fisher-Price Rock 'n Play, Kids II rockers, and all other inclined infant sleepers (banned by the Safe Sleep for Babies Act of 2022)</li>
            <li><strong>Drop-side cribs:</strong> Banned by the CPSC in 2011 due to suffocation and strangulation hazards</li>
            <li><strong>Crib bumpers:</strong> Padded crib bumpers (banned by the Safe Sleep for Babies Act of 2022)</li>
            <li><strong>Expired car seats:</strong> Car seats past their manufacturer expiration date (typically 6–10 years from manufacture)</li>
            <li><strong>Car seats involved in a crash:</strong> Must be replaced after any moderate-to-severe collision</li>
            <li><strong>Used breast pumps:</strong> Personal-use (single-user) pumps cannot be resold due to FDA regulations and hygiene concerns. Hospital-grade multi-user pumps are allowed.</li>
            <li><strong>Used bottle nipples, pacifiers, or teethers:</strong> For hygiene reasons</li>
            <li><strong>Any product with an active CPSC recall:</strong> Check the <a href="https://www.cpsc.gov/Recalls" target="_blank" rel="noopener noreferrer" style={linkStyle}>CPSC Recalls database</a> before listing</li>
            <li><strong>Formula, food, or medication:</strong> Including expired or opened items</li>
            <li>Items with damaged, broken, or missing safety features (harnesses, locks, straps)</li>
            <li>Counterfeit or unlicensed goods</li>
            <li>Illegal items or items promoting illegal activity</li>
            <li>Non-baby-related items (BabyResell is for baby and kids' products only)</li>
          </ul>
          <p style={paragraphStyle}>
            <strong>Sellers are responsible</strong> for checking recall status before listing. When in doubt, consult the <a href="https://www.cpsc.gov/Recalls" target="_blank" rel="noopener noreferrer" style={linkStyle}>Consumer Product Safety Commission (CPSC)</a> or <a href="https://www.nhtsa.gov/equipment/car-seats-and-booster-seats" target="_blank" rel="noopener noreferrer" style={linkStyle}>NHTSA car seat safety guide</a>.
          </p>

          <h3 style={subsectionTitleStyle}>Prohibited Conduct:</h3>
          <ul style={listStyle}>
            <li>Fraud, scams, or misrepresentation</li>
            <li>Harassment, threats, or abusive behavior</li>
            <li>Requesting payment outside the platform</li>
            <li>Manipulating reviews or ratings</li>
            <li>Scraping or copying platform content</li>
            <li>Circumventing security measures or platform fees</li>
            <li>Creating multiple accounts to abuse the platform</li>
          </ul>
        </div>

        {/* Intellectual Property */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Intellectual Property</h2>
          <p style={paragraphStyle}>
            BabyResell and all related logos, designs, and content are the property of Interverse and are protected by copyright, trademark, and other intellectual property laws.
          </p>
          <p style={paragraphStyle}>
            By posting content (photos, descriptions, reviews), you grant BabyResell a worldwide, non-exclusive, royalty-free license to use, display, and distribute that content on the Platform.
          </p>
        </div>

        {/* Limitation of Liability */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Limitation of Liability</h2>
          <p style={paragraphStyle}>
            BabyResell is a marketplace platform that facilitates transactions between buyers and sellers. We do not own, manufacture, or inspect the items listed for sale.
          </p>
          <p style={paragraphStyle}>
            TO THE FULLEST EXTENT PERMITTED BY LAW, BABYRESELL AND INTERVERSE ARE NOT LIABLE FOR:
          </p>
          <ul style={listStyle}>
            <li>Quality, safety, legality, or condition of items sold</li>
            <li>Accuracy of listings or user-generated content</li>
            <li>Actions or conduct of buyers, sellers, or other users</li>
            <li>Lost profits, data, or indirect damages</li>
            <li>Issues arising from shipment or delivery</li>
          </ul>
          <p style={paragraphStyle}>
            Our total liability to you shall not exceed the fees you paid to BabyResell in the 12 months prior to the claim.
          </p>
        </div>

        {/* Indemnification */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Indemnification</h2>
          <p style={paragraphStyle}>
            You agree to indemnify and hold harmless BabyResell, Interverse, and our employees from any claims, damages, or expenses arising from:
          </p>
          <ul style={listStyle}>
            <li>Your use of the Platform</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any laws or third-party rights</li>
            <li>Items you list, sell, or purchase</li>
          </ul>
        </div>

        {/* Termination */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Termination</h2>
          <p style={paragraphStyle}>
            We reserve the right to suspend or terminate your account at any time for violations of these Terms, suspected fraud, or any other reason at our discretion.
          </p>
          <p style={paragraphStyle}>
            You may close your account at any time through your account settings. Upon termination, you remain liable for any outstanding obligations.
          </p>
        </div>

        {/* Changes to Terms */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Changes to These Terms</h2>
          <p style={paragraphStyle}>
            We may update these Terms from time to time. When we make material changes, we will notify you via email or a notice on the Platform. Continued use of BabyResell after changes constitutes acceptance of the updated Terms.
          </p>
        </div>

        {/* Dispute Resolution */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Dispute Resolution</h2>
          <p style={paragraphStyle}>
            If you have a dispute with another user, we encourage you to contact them directly first. If that doesn't resolve the issue, you may open a dispute through our Platform within 48 hours of delivery.
          </p>
          <p style={paragraphStyle}>
            For disputes with BabyResell, you agree to first attempt to resolve the issue through our <a href="/contact" style={linkStyle}>support team</a>.
          </p>
        </div>

        {/* Governing Law */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Governing Law</h2>
          <p style={paragraphStyle}>
            These Terms are governed by the laws of the United States and the State of Washington, without regard to conflict of law principles.
          </p>
        </div>

        {/* Contact */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Contact Us</h2>
          <p style={paragraphStyle}>
            If you have questions about these Terms, please contact us:
          </p>
          <ul style={{ ...listStyle, listStyle: 'none', paddingLeft: 0 }}>
            <li>
              Email:{' '}
              <a href="mailto:legal@interverse.app" style={linkStyle}>
                legal@interverse.app
              </a>
            </li>
            <li>
              Support:{' '}
              <a href="/contact" style={linkStyle}>
                Contact Support
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Terms;
